/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IPromoContainerData } from './promo-container.data';
import { IPromoContainerProps } from './promo-container.props.autogenerated';
import { IModuleStateManager, IModuleStateProps } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { get } from 'lodash';
import { convertStringToArray } from '../../utils/alternateWarehouseHelper';
import { IGetCustomerFreightTermCodeData } from '../../actions/get-customer-freight-term-code.action';

export interface IPromoContainerPropsExt extends IPromoContainerProps<IPromoContainerData>, IModuleStateProps {
    enableControl: boolean;
}

export interface IPromoContainerViewProps extends IPromoContainerProps<IPromoContainerData> {
    items: IPromoContainerItemProps[];
    moduleState: IModuleStateManager;
    promoContainerModuleProps: IModuleProps;
}

export interface IPromoContainerItemProps {
    id: string;
    itemProps: INodeProps;
    item: React.ReactNode;
}

/**
 *
 * PromoContainer component
 * @extends {React.PureComponent<IPromoContainerProps<IPromoContainerData>>}
 */
class PromoContainer extends React.PureComponent<IPromoContainerPropsExt> {
    public constructor(props: IPromoContainerPropsExt) {
        super(props);
    }

    public render(): JSX.Element | null {
        const { data } = this.props;
        const customerFreightTerm = data.customerFreightTerm.result;

        if (data.customerFreightTerm.status === 'LOADING') {
            return null;
        }

        const items = this.getPromoOptionContainerModule(customerFreightTerm);

        const viewProps: IPromoContainerViewProps = {
            ...this.props,
            items: items.map((item: React.ReactNode) => {
                const childId = get(item, 'props.id') || '';
                return {
                    id: childId,
                    itemProps: {
                        className: ''
                    },
                    item
                };
            }),
            promoContainerModuleProps: {
                moduleProps: this.props,
                className: this.props.config.className || 'gdn-promo-container'
            }
        };

        return this.props.renderView(viewProps);
    }

    private readonly getPromoOptionContainerModule = (
        customerFreightTerm: IGetCustomerFreightTermCodeData | undefined
    ): React.ReactNode[] => {
        const { slots } = this.props;

        if (!slots?.primary?.length) {
            return [];
        }

        const freightTerm = customerFreightTerm?.freightTermCodeResp?.GdnFreightTermCodeId ?? '';
        const customerGroup = customerFreightTerm?.freightTermCodeResp?.GdnCustomerGroup ?? '';
        const defaultWarehouse = customerFreightTerm?.freightTermCodeResp?.DefaultWarehouse ?? '';

        const matches = (slot: React.ReactElement): boolean => {
            const config = slot.props.config || {};
            const freightTermCodes = convertStringToArray(config.allowableFreightTermCode);
            const customerGroups = convertStringToArray(config.allowableCustomerGroup);
            const warehouses = convertStringToArray(config.allowableDefaultWarehouse);
            const bothEmpty = freightTermCodes.length === 0 && customerGroups.length === 0 && warehouses.length === 0;

            return (
                (freightTermCodes.length === 0 || freightTermCodes.includes(freightTerm)) &&
                (customerGroups.length === 0 || customerGroups.includes(customerGroup)) &&
                (warehouses.length === 0 || warehouses.includes(defaultWarehouse)) &&
                !bothEmpty
            );
        };

        // 1 Customer Group + Freight Term Code + Default Warehouse
        const groupFreightWarehouseMatch = slots.primary.find(slot => {
            const config = (slot as React.ReactElement).props.config || {};
            const freightTermCodes = convertStringToArray(config.allowableFreightTermCode);
            const customerGroups = convertStringToArray(config.allowableCustomerGroup);
            const warehouses = convertStringToArray(config.allowableDefaultWarehouse);
            return freightTermCodes.length > 0 && customerGroups.length > 0 && warehouses.length > 0 && matches(slot as React.ReactElement);
        });
        if (groupFreightWarehouseMatch) {
            return [groupFreightWarehouseMatch];
        }

        // 2 Customer Group + Default Warehouse
        const groupWarehouseMatch = slots.primary.find(slot => {
            const config = (slot as React.ReactElement).props.config || {};
            const customerGroups = convertStringToArray(config.allowableCustomerGroup);
            const warehouses = convertStringToArray(config.allowableDefaultWarehouse);
            const freightTermCodes = convertStringToArray(config.allowableFreightTermCode);
            return (
                customerGroups.length > 0 && warehouses.length > 0 && freightTermCodes.length === 0 && matches(slot as React.ReactElement)
            );
        });
        if (groupWarehouseMatch) {
            return [groupWarehouseMatch];
        }

        // 3 Customer Group + Freight Term Code
        const groupFreightMatch = slots.primary.find(slot => {
            const config = (slot as React.ReactElement).props.config || {};
            const customerGroups = convertStringToArray(config.allowableCustomerGroup);
            const freightTermCodes = convertStringToArray(config.allowableFreightTermCode);
            const warehouses = convertStringToArray(config.allowableDefaultWarehouse);
            return (
                customerGroups.length > 0 && freightTermCodes.length > 0 && warehouses.length === 0 && matches(slot as React.ReactElement)
            );
        });
        if (groupFreightMatch) {
            return [groupFreightMatch];
        }

        // 4 Default Warehouse only
        const warehouseMatch = slots.primary.find(slot => {
            const config = (slot as React.ReactElement).props.config || {};
            const warehouses = convertStringToArray(config.allowableDefaultWarehouse);
            const customerGroups = convertStringToArray(config.allowableCustomerGroup);
            const freightTermCodes = convertStringToArray(config.allowableFreightTermCode);
            return (
                warehouses.length > 0 && customerGroups.length === 0 && freightTermCodes.length === 0 && matches(slot as React.ReactElement)
            );
        });
        if (warehouseMatch) {
            return [warehouseMatch];
        }

        // 5 Customer Group only
        const groupMatch = slots.primary.find(slot => {
            const config = (slot as React.ReactElement).props.config || {};
            const customerGroups = convertStringToArray(config.allowableCustomerGroup);
            const freightTermCodes = convertStringToArray(config.allowableFreightTermCode);
            const warehouses = convertStringToArray(config.allowableDefaultWarehouse);
            return (
                customerGroups.length > 0 && freightTermCodes.length === 0 && warehouses.length === 0 && matches(slot as React.ReactElement)
            );
        });
        if (groupMatch) {
            return [groupMatch];
        }

        // 6 Freight Term Code only
        const freightMatch = slots.primary.find(slot => {
            const config = (slot as React.ReactElement).props.config || {};
            const freightTermCodes = convertStringToArray(config.allowableFreightTermCode);
            const customerGroups = convertStringToArray(config.allowableCustomerGroup);
            const warehouses = convertStringToArray(config.allowableDefaultWarehouse);
            return (
                freightTermCodes.length > 0 && customerGroups.length === 0 && warehouses.length === 0 && matches(slot as React.ReactElement)
            );
        });
        if (freightMatch) {
            return [freightMatch];
        }

        // 7 Default promo
        const defaultSlot = slots.primary.find(slot => {
            const config = (slot as React.ReactElement).props.config || {};
            return config.isDefaultContainer;
        });
        return defaultSlot ? [defaultSlot] : [];
    };
}

export default PromoContainer;
